import React, {
  ReactElement, useEffect, useState,
} from 'react';
import ReactDOM from 'react-dom';
import { Carousel } from 'react-responsive-carousel';
import { cloudinaryTransform } from '../../utils/cloudinaryTransformations';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import useWindowDimensions from '../../hooks/useWindowDimensions';

interface Slide {
  id: number;
  subtitle: string;
  title: string;
  button_url: string;
  button_label: string;
  backgroundImageMobileUrl: string;
  backgroundImageTabletUrl: string;
  backgroundImageUrl: string;
}

type SlideSize = 'mobile' | 'tablet' | 'desktop';

interface HomeSliderProps {
  slides: Slide[];
}

const HomeSlider: React.FC<HomeSliderProps> = ({ slides }): ReactElement => {
  const dimensions = useWindowDimensions();
  const [screenSize, setScreenSize] = useState<SlideSize>('desktop');
  const [cropArea, setCropArea] = useState<string>('');

  useEffect(() => {
    const { width } = dimensions;
    let size: SlideSize = 'desktop';
    let crop: string = 'w_1920,h_530';

    if (width < 768) {
      size = 'mobile';
      crop = 'w_767,h_530';
    } else if (width >= 768 && width < 992) {
      size = 'tablet';
      crop = 'w_991,h_530';
    }

    setScreenSize(size);
    setCropArea(crop);
  }, [dimensions]);

  const getBackgroundImage = (slide: Slide) => {
    let backgroundImage: string = '';

    switch (screenSize) {
      case 'mobile':
        backgroundImage = slide.backgroundImageMobileUrl;
        break;
      case 'tablet':
        backgroundImage = slide.backgroundImageTabletUrl;
        break;
      default:
        backgroundImage = slide.backgroundImageUrl;
    }

    return cloudinaryTransform(
      backgroundImage,
      [
        't_home-slider',
        `c_fill,${cropArea},g_auto`,
        'g_auto',
      ],
    );
  };

  return (
    <Carousel
      swipeScrollTolerance={150}
      preventMovementUntilSwipeScrollTolerance={true}
      showThumbs={false}
      autoPlay={true}
      interval={5000}
      infiniteLoop={true}
    >
      {slides.map((slide: Slide) => (
        <div key={slide.id}>
          <div
            className="jumbotron jumbotron-fluid main-slider"
            style={{
              backgroundImage: `url(${getBackgroundImage(slide)})`,
            }}
          >
            <div className="container h-100">
              <div className="row h-100">
                <div className="col-12 col--sm-12 col-md-12 col-lg-7 col-xl-7 my-auto">
                  <div className="text-md-left text-center">
                    <p className="text-white">{slide.subtitle}</p>
                    <h1 className="text-white">{slide.title}</h1>
                    <a
                      href={slide.button_url}
                      className="btn btn-lg btn-beige btn-cta text-uppercase"
                    >
                      {slide.button_label}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Carousel>
  );
};

export default HomeSlider;

const initializeHomeSlider = () => {
  const sliderElements = document.querySelectorAll('.react-homepage-slider');
  sliderElements.forEach((element) => {
    const slides = JSON.parse(element.getAttribute('data-slides') || '[]');
    ReactDOM.render(<HomeSlider slides={slides} />, element);
  });
};

if (document.getElementsByClassName('react-homepage-slider').length > 0) {
  initializeHomeSlider();
}
